import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useOrg } from '../../hooks/useOrg';
import { api } from '../../lib/api';
import { isFirmLike } from '../../lib/orgTypes';

import { displayEmail, isPlaceholderEmail } from '../../lib/contactEmail';
import LoadingSpinner from '../shared/LoadingSpinner';
import ConfirmDialog from '../shared/ConfirmDialog';

// Contacts are people. For a law firm the matters themselves live under
// Cases (/app/cases); this list only shows which cases each person has.
export default function ContactsPage() {
  const { currentOrg } = useOrg();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', sms_opt_in: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [inviting, setInviting] = useState(null);
  const [inviteSuccess, setInviteSuccess] = useState(null);
  const [casesByClient, setCasesByClient] = useState({}); // law firms: every open case per person, newest first

  const handleInvite = async (contact) => {
    setInviting(contact.id);
    try {
      await api.inviteContact(currentOrg.id, contact.id);
      setInviteSuccess(contact.id);
      setTimeout(() => setInviteSuccess(null), 3000);
    } catch (err) {
      alert(err.message);
    } finally {
      setInviting(null);
    }
  };

  const fetchContacts = useCallback(async () => {
    if (!currentOrg) return;
    try {
      const data = await api.getContacts(currentOrg.id);
      setContacts(data);
      if (isFirmLike(currentOrg.type)) {
        api.listCases(currentOrg.id).then((list) => {
          const m = {};
          for (const cs of list || []) (m[cs.client_id] ||= []).push(cs);
          setCasesByClient(m);
        }).catch(() => {});
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentOrg]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const isFirm = isFirmLike(currentOrg?.type);

  const filtered = contacts.filter(c => {
    if (!search) return true;
    const term = search.toLowerCase();
    return `${c.first_name} ${c.last_name}`.toLowerCase().includes(term)
      || c.email?.toLowerCase().includes(term)
      || c.phone?.includes(term);
  });

  const resetForm = () => {
    setForm({ first_name: '', last_name: '', email: '', phone: '' });
    setShowForm(false);
    setEditId(null);
    setError('');
  };

  const startEdit = (c) => {
    setEditId(c.id);
    setForm({ first_name: c.first_name, last_name: c.last_name, email: c.email || '', phone: c.phone || '', sms_opt_in: !!c.sms_opt_in });
    setShowForm(true);
    setError('');
  };

  const handleSave = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setError('First and last name are required');
      return;
    }
    if (form.email.trim() && !form.email.includes('@')) {
      setError('That email address does not look valid');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editId) {
        await api.updateContact(currentOrg.id, editId, form);
      } else {
        await api.createContact(currentOrg.id, form);
      }
      resetForm();
      await fetchContacts();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.deleteContact(currentOrg.id, confirmDelete.id);
      setConfirmDelete(null);
      await fetchContacts();
    } catch (err) {
      console.error(err);
    }
  };

  const inputClass = 'w-full px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13, 167, 202,0.15)]';

  return (
    <div className="flex-1 bg-primary">
      <div className="max-w-[900px] mx-auto px-4 md:px-7 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-[22px] font-bold text-primary-c">Contacts</h1>
            {isFirm && (
              <p className="text-[12.5px] text-muted-c mt-0.5">
                The people on your cases and how to reach them. The matters themselves live under <Link to="/app/cases" className="text-accent-light font-semibold">Cases</Link>.
              </p>
            )}
          </div>
          {!showForm && (
            <button
              onClick={() => { setShowForm(true); setEditId(null); setForm({ first_name: '', last_name: '', email: '', phone: '' }); }}
              className="flex items-center gap-2 px-4 py-2.5 gradient-accent text-white rounded-[10px] text-[13px] font-semibold shadow-glow hover:-translate-y-0.5 transition-all"
            >
              <span className="text-lg leading-none">+</span> <span className="whitespace-nowrap">Add Contact</span>
            </button>
          )}
        </div>

        {/* Add/Edit form */}
        {showForm && (
          <div className="bg-elevated border border-theme rounded-xl p-5 mb-5 animate-fade-in">
            <h3 className="text-[15px] font-semibold text-primary-c mb-4">
              {editId ? 'Edit Contact' : 'New Contact'}
            </h3>
            {error && <div className="px-4 py-3 rounded-[10px] bg-danger-soft text-danger text-[13px] font-medium mb-4">{error}</div>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 mb-4">
              <div>
                <label className="block text-[12px] font-semibold text-secondary-c mb-2">First Name *</label>
                <input type="text" className={inputClass} value={form.first_name} onChange={e => setForm(p => ({ ...p, first_name: e.target.value }))} autoFocus />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-secondary-c mb-2">Last Name *</label>
                <input type="text" className={inputClass} value={form.last_name} onChange={e => setForm(p => ({ ...p, last_name: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 mb-4">
              <div>
                <label className="block text-[12px] font-semibold text-secondary-c mb-2">Email <span className="text-muted-c font-normal">(optional — needed for portal access)</span></label>
                <input type="email" className={inputClass} value={isPlaceholderEmail(form.email) ? '' : form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="email@example.com" />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-secondary-c mb-2">Phone</label>
                <input type="tel" className={inputClass} value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="(555) 555-5555" />
                <label className="flex items-start gap-2 mt-2 cursor-pointer">
                  <input type="checkbox" checked={!!form.sms_opt_in} onChange={e => setForm(p => ({ ...p, sms_opt_in: e.target.checked }))} className="mt-0.5 accent-[#0DA7CA]" />
                  <span className="text-[11.5px] text-muted-c leading-snug">The patient agreed to receive appointment and referral texts at this number (booking confirmations, reminders, changes). No medical details are ever texted. They can reply STOP any time.</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2.5 pt-4 border-t border-theme">
              <button onClick={resetForm} className="px-5 py-2.5 rounded-[10px] text-[13px] font-semibold text-primary-c bg-tertiary border border-theme hover:bg-hover-surface transition-all">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="px-5 py-2.5 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50">
                {saving ? 'Saving...' : editId ? 'Save Changes' : 'Add Contact'}
              </button>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="mb-4">
          <input
            type="text"
            className={inputClass}
            placeholder="Search contacts..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {/* List */}
        {loading ? (
          <div className="bg-elevated rounded-xl border border-theme overflow-hidden">
            {/* Table header skeleton */}
            <div className="flex items-center px-5 py-3 border-b border-theme gap-5">
              <div className="h-3 w-16 bg-tertiary rounded animate-pulse" />
              <div className="h-3 w-20 bg-tertiary rounded animate-pulse hidden md:block ml-auto" />
              <div className="h-3 w-16 bg-tertiary rounded animate-pulse hidden sm:block" />
              <div className="h-3 w-12 bg-tertiary rounded animate-pulse" />
            </div>
            {/* Row skeletons */}
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex items-center px-5 py-3.5 border-b border-theme gap-5" style={{ animationDelay: `${i * 60}ms` }}>
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-[34px] h-[34px] rounded-[10px] bg-tertiary animate-pulse flex-shrink-0" />
                  <div className="h-3.5 bg-tertiary rounded animate-pulse" style={{ width: `${100 + Math.random() * 80}px` }} />
                </div>
                <div className="h-3 w-36 bg-tertiary rounded animate-pulse hidden md:block" />
                <div className="h-3 w-28 bg-tertiary rounded animate-pulse hidden sm:block" />
                <div className="flex gap-2">
                  <div className="h-3 w-8 bg-tertiary rounded animate-pulse" />
                  <div className="h-3 w-10 bg-tertiary rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-elevated rounded-xl border border-theme px-5 py-12 text-center">
            <p className="text-[14px] text-muted-c">
              {search ? 'No contacts match your search.' : 'No contacts yet. Add your first contact to get started.'}
            </p>
          </div>
        ) : (
          <div className="bg-elevated rounded-xl border border-theme overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-theme">
                    <th className="text-left text-[11px] font-semibold text-muted-c uppercase tracking-wider px-5 py-3">Name</th>
                    <th className="text-left text-[11px] font-semibold text-muted-c uppercase tracking-wider px-5 py-3 hidden md:table-cell">Email</th>
                    <th className="text-left text-[11px] font-semibold text-muted-c uppercase tracking-wider px-5 py-3 hidden sm:table-cell">Phone</th>
                    {isFirm && <th className="text-left text-[11px] font-semibold text-muted-c uppercase tracking-wider px-5 py-3 hidden md:table-cell">Cases</th>}
                    <th className="text-right text-[11px] font-semibold text-muted-c uppercase tracking-wider px-5 py-3 w-24">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border)]">
                  {filtered.map(c => {
                    const initials = `${(c.first_name || '?')[0]}${(c.last_name || '?')[0]}`.toUpperCase();
                    return (
                      <tr key={c.id} className="hover:bg-hover-surface transition-all">
                        <td className="px-5 py-3.5">
                          <Link to={`/app/contacts/${c.id}`} className="flex items-center gap-3 group">
                            <div className="w-[34px] h-[34px] rounded-[10px] bg-accent-glow flex items-center justify-center text-[11px] font-bold text-accent-light flex-shrink-0">
                              {initials}
                            </div>
                            <span className="min-w-0">
                              <span className="block text-[13px] font-semibold text-primary-c group-hover:text-accent-light transition-colors">{c.first_name} {c.last_name}</span>
                              {isFirm && (casesByClient[c.id] || []).length > 0 && (
                                <span className="md:hidden flex flex-wrap gap-1 mt-1">
                                  {casesByClient[c.id].map(cs => <span key={cs.id} className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold ${['settled', 'closed'].includes(cs.status) ? 'bg-tertiary text-muted-c' : 'bg-accent-glow text-accent-light'}`}>{cs.case_number}</span>)}
                                </span>
                              )}
                            </span>
                          </Link>
                        </td>
                        <td className="px-5 py-3.5 hidden md:table-cell text-[13px] text-secondary-c">
                          {c.email?.includes('@contact.local') ? '—' : c.email}
                        </td>
                        <td className="px-5 py-3.5 hidden sm:table-cell text-[13px] text-secondary-c">{c.phone || '—'}</td>
                        {isFirm && (
                          <td className="px-5 py-3.5 hidden md:table-cell">
                            {(casesByClient[c.id] || []).length ? (
                              <div className="flex flex-wrap gap-1.5">
                                {casesByClient[c.id].map(cs => (
                                  <Link key={cs.id} to={`/app/cases/${cs.id}`} className={`inline-block px-2 py-0.5 rounded-md text-[10.5px] font-mono font-semibold ${['settled', 'closed'].includes(cs.status) ? 'bg-tertiary text-muted-c' : 'bg-accent-glow text-accent-light'} hover:opacity-80 whitespace-nowrap`}>
                                    {cs.case_number}
                                  </Link>
                                ))}
                              </div>
                            ) : (
                              <Link to={`/app/cases/new?client=${c.id}`} className="text-[12px] font-medium text-accent-light hover:text-accent whitespace-nowrap">Start a case</Link>
                            )}
                          </td>
                        )}
                        <td className="px-5 py-3.5 text-right">
                          <div className="flex justify-end gap-2">
                            {c.email && !c.email.includes('@contact.local') && (
                              inviteSuccess === c.id ? (
                                <span className="text-[12px] font-medium text-success">Sent!</span>
                              ) : (
                                <button
                                  onClick={() => handleInvite(c)}
                                  disabled={inviting === c.id}
                                  className="text-[12px] font-medium text-accent-light hover:text-accent transition-colors disabled:opacity-50"
                                >
                                  {inviting === c.id ? 'Sending...' : 'Invite'}
                                </button>
                              )
                            )}
                            <button onClick={() => startEdit(c)} className="text-[12px] font-medium text-muted-c hover:text-accent-light transition-colors">Edit</button>
                            <button onClick={() => setConfirmDelete(c)} className="text-[12px] font-medium text-muted-c hover:text-danger transition-colors">Delete</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <p className="text-[11px] text-muted-c mt-3">
          {contacts.length} contact{contacts.length !== 1 ? 's' : ''} total
        </p>
      </div>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Remove Contact"
        message={`Remove ${confirmDelete?.first_name} ${confirmDelete?.last_name} from your contacts?`}
        confirmLabel="Remove"
        danger
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
